import { IterableMap } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentGroupData } from "@redotech/redo-model/fulfillments/fulfillment-group";
import {
  FulfillmentOrderLineItem,
  groupFulfillmentOrderLineItems,
} from "@redotech/redo-model/fulfillments/fulfillment-order-line-item";
import {
  RedoModal,
  RedoModalProps,
} from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoRadioButton } from "@redotech/redo-web/arbiter-components/radio/redo-radio-button";
import {
  RedoTable,
  RedoTableProps,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import {
  RedoTablePaddedCell,
  RedoTableTextCell,
} from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { FulfillmentGroupStatusBadge } from "../../common/status-badge";
import * as styles from "./multiple-order-select-modal.module.css";

export const FulfillmentGroupSelectModal = memo(
  function FulfillmentGroupSelectModal({
    open,
    identifier,
    onClose,
    onSelect,
    relevantFulfillmentGroups,
  }: {
    open: boolean;
    identifier: string;
    onClose: () => void;
    onSelect: (fulfillmentGroup: FulfillmentGroupData) => void;
    relevantFulfillmentGroups: FulfillmentGroupData[];
  }) {
    const [selectedGroup, setSelectedGroup] =
      useState<FulfillmentGroupData | null>(null);

    const handleSelect = useHandler(
      (fulfillmentGroup: FulfillmentGroupData) => {
        setSelectedGroup(fulfillmentGroup);
      },
    );

    const modalProps: RedoModalProps = {
      isOpen: open,
      onModalCloseRequested: onClose,
      title: `Multiple shipments found for order ${identifier}`,
      subtitle: "Select the shipment you'd like to fulfill.",
      primaryButton: {
        text: "Select shipment",
        disabled: !selectedGroup,
        onClick: () => {
          if (selectedGroup) {
            onSelect(selectedGroup);
          }
        },
      },
      contentScrollable: true,
      secondaryButton: { text: "Cancel", onClick: onClose },
    };

    return (
      <RedoModal {...modalProps}>
        <Flex dir="column" gap="md" py="lg">
          <IterableMap
            items={relevantFulfillmentGroups}
            keyFn={(item) => item._id.toString()}
          >
            {(group) => (
              <SelectListItem
                group={group}
                key={group._id.toString()}
                onSelect={handleSelect}
                selected={selectedGroup === group}
              />
            )}
          </IterableMap>
        </Flex>
      </RedoModal>
    );
  },
);

const SelectListItem = memo(function SelectListItem({
  group,
  onSelect,
  selected,
}: {
  group: FulfillmentGroupData;
  onSelect: (fulfillmentGroup: FulfillmentGroupData) => void;
  selected: boolean;
}) {
  const getUniqueLineItems = (group: FulfillmentGroupData) => {
    const lineItems = group.items;
    return groupFulfillmentOrderLineItems(lineItems);
  };

  const tableProps: RedoTableProps<FulfillmentOrderLineItem> = {
    layout: {
      columns: [
        {
          renderHeaderCell: () => (
            <StandardRedoTableHeaderCell headerText="Item" />
          ),
          renderNormalCell: ({ row }) => (
            <RedoTablePaddedCell>
              <Flex align="center" gap="sm">
                <img
                  alt={row.title}
                  className={styles.image}
                  src={row.imageUrl ?? ""}
                />
                <Flex dir="column" gap="none">
                  <Text
                    fontSize="xs"
                    textColor="tertiary"
                    textOverflow="ellipsis"
                  >
                    {row.productTitle}
                  </Text>
                  <Text
                    fontSize="xxs"
                    textColor="secondary"
                    textOverflow="ellipsis"
                  >
                    {row.variantTitle}
                  </Text>
                </Flex>
              </Flex>
            </RedoTablePaddedCell>
          ),
        },
        {
          renderHeaderCell: () => (
            <StandardRedoTableHeaderCell headerText="SKU" />
          ),
          renderNormalCell: ({ row }) => (
            <RedoTableTextCell text={row.sku ?? ""} />
          ),
          widthMode: { type: "fixed", width: "150px" },
        },
        {
          renderHeaderCell: () => (
            <StandardRedoTableHeaderCell headerText="Price" />
          ),
          renderNormalCell: ({ row }) => (
            <RedoTableTextCell text={`$${row.totalPrice.amount}`} />
          ),
          widthMode: { type: "fixed", width: "100px" },
        },
        {
          renderHeaderCell: () => (
            <StandardRedoTableHeaderCell headerText="Quantity" />
          ),
          renderNormalCell: ({ row }) => (
            <RedoTableTextCell text={row.quantity.toString()} />
          ),
          widthMode: { type: "fixed", width: "100px" },
        },
      ],
    },
    rowsOfData: getUniqueLineItems(group),
  };

  return (
    <Card
      className={selected ? styles.cardSelected : ""}
      onClick={() => onSelect(group)}
    >
      <Flex dir="column" gap="md">
        <Flex justify="space-between">
          <Flex data-testid={`fulfillment-group-${group._id}`}>
            <RedoRadioButton
              onSelect={() => onSelect(group)}
              selected={selected}
            />
            <Text textColor="secondary">
              {group.orders.map((order) => order.name).join(", ")}
            </Text>
          </Flex>
          <FulfillmentGroupStatusBadge fulfillmentGroupStatus={group.status} />
        </Flex>
        <Flex justify="space-between">
          <Flex dir="column" gap="none">
            <Text fontSize="xs" textColor="tertiary">
              Customer name
            </Text>
            <Text fontSize="xs" textColor="primary">
              {group.customer?.name}
            </Text>
          </Flex>
          <Flex dir="column" gap="none">
            <Text fontSize="xs" textColor="tertiary">
              Shipping address
            </Text>
            <Flex dir="column" gap="none">
              <Text fontSize="xs" textColor="primary">
                {group.destinationAddress?.address1}
              </Text>
              {group.destinationAddress?.address2 && (
                <Text fontSize="xs" textColor="primary">
                  {group.destinationAddress?.address2}
                </Text>
              )}
              <Text fontSize="xs" textColor="primary">
                {group.destinationAddress?.city},{" "}
                {group.destinationAddress?.province}
              </Text>
              <Text fontSize="xs" textColor="primary">
                {group.destinationAddress?.zip},{" "}
                {group.destinationAddress?.countryCode}
              </Text>
            </Flex>
          </Flex>
          <Flex>
            <Flex dir="column" gap="none">
              <Text fontSize="xs" textColor="tertiary">
                Ship from
              </Text>
              <Text fontSize="xs" textColor="primary">
                {group.originAddress?.name}
              </Text>
            </Flex>
          </Flex>
        </Flex>
        <RedoTable
          additionalClasses={styles.table}
          layout={tableProps.layout}
          rowsOfData={tableProps.rowsOfData}
        />
      </Flex>
    </Card>
  );
});
